<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <base-widget
    :icon="widgetIcon"
    class="public-holiday-widget"
    :empty="isEmpty && viewMode === 'list'"
    :empty-text="$t('dashboard.no_upcoming_public_holidays')"
    :loading="isLoading"
    :title="widgetTitle"
  >
    <template #action>
      <div class="public-holiday-widget-actions">
        <button
          type="button"
          class="public-holiday-widget-action-link"
          :title="$t('dashboard.holiday_ics_feed')"
          @click="copyHolidayIcsFeedUrl"
        >
          <oxd-icon name="calendar-plus" />
        </button>
        <a
          v-if="googleCalendarSubscribeUrl"
          class="public-holiday-widget-action-link"
          :href="googleCalendarSubscribeUrl"
          target="_blank"
          rel="noopener noreferrer"
          :title="$t('dashboard.add_to_google_calendar')"
        >
          <oxd-icon name="google" />
        </a>
        <button
          type="button"
          class="public-holiday-widget-view-switch"
          :class="{'--calendar': isCalendarView}"
          :title="$t('dashboard.calendar_view')"
          :aria-pressed="isCalendarView"
          @click="isCalendarView = !isCalendarView"
        >
          <span class="public-holiday-widget-view-switch-track">
            <span class="public-holiday-widget-view-switch-thumb"></span>
          </span>
        </button>
      </div>
    </template>

    <div
      v-if="viewMode === 'calendar'"
      class="public-holiday-widget-calendar"
      @click="onCalendarBackgroundClick"
    >
      <oxd-calendar
        v-model="selectedDate"
        :events="events"
        :day-attributes="dayAttributes"
        :first-day-of-week="firstDayOfWeek"
        :locale="locale"
        :years="years"
        @update:model-value="onCalendarDateSelect"
        @select-month="onVisibleMonthChange"
        @select-year="onVisibleMonthChange"
      />
      <div
        v-if="holidayTooltip"
        ref="holidayTooltipRef"
        class="public-holiday-widget-tooltip"
        :class="{'--below': holidayTooltip.placement === 'bottom'}"
        :style="holidayTooltipStyle"
      >
        <oxd-text tag="p" class="public-holiday-widget-tooltip-name">
          {{ holidayTooltip.primary }}
        </oxd-text>
        <oxd-text
          v-for="(line, index) in holidayTooltip.extraLines"
          :key="index"
          tag="p"
          class="public-holiday-widget-tooltip-extra"
        >
          {{ line }}
        </oxd-text>
        <oxd-text
          v-if="holidayTooltip.formattedDate"
          tag="p"
          class="public-holiday-widget-tooltip-date"
        >
          {{ holidayTooltip.formattedDate }}
        </oxd-text>
      </div>
    </div>

    <div v-else class="public-holiday-widget-list">
      <section
        v-for="group in groupedHolidays"
        :key="group.key"
        class="public-holiday-widget-list-group"
      >
        <oxd-text tag="p" class="public-holiday-widget-list-group-title">
          {{ group.label }}
        </oxd-text>
        <div
          v-for="holiday in group.holidays"
          :key="`${holiday.id}-${holiday.date}`"
          class="public-holiday-widget-list-item"
          :class="{
            '--highlighted': holiday.date === highlightedHolidayDate,
          }"
          :data-holiday-date="holiday.date"
        >
          <div class="public-holiday-widget-list-marker"></div>
          <div class="public-holiday-widget-list-details">
            <oxd-text tag="p" class="public-holiday-widget-list-name">
              {{ holiday.name }}
            </oxd-text>
            <oxd-text tag="p" class="public-holiday-widget-list-meta">
              {{ formatHolidayDayLabel(holiday.date) }}
            </oxd-text>
          </div>
        </div>
      </section>
    </div>
  </base-widget>
</template>

<script>
import {APIService} from '@/core/util/services/api.service';
import BaseWidget from '@/orangehrmDashboardPlugin/components/BaseWidget.vue';
import useDateFormat from '@/core/util/composable/useDateFormat';
import useLocale from '@/core/util/composable/useLocale';
import {formatDate, parseDate} from '@ohrm/core/util/helper/datefns';
import {yearRange} from '@ohrm/core/util/helper/year-range';
import {navigateToBuzzNotification} from '@/orangehrmBuzzPlugin/util/buzzNotificationNavigation';
import {OxdCalendar, OxdIcon} from '@ohrm/oxd';

export default {
  name: 'PublicHolidayWidget',

  components: {
    'base-widget': BaseWidget,
    'oxd-calendar': OxdCalendar,
    'oxd-icon': OxdIcon,
  },

  props: {
    googleCalendarEmbedUrl: {
      type: String,
      default: null,
    },
  },

  setup() {
    const {jsDateFormat} = useDateFormat();
    const {locale} = useLocale();
    const http = new APIService(window.appGlobal.baseUrl, '');

    return {
      http,
      jsDateFormat,
      locale,
      years: [...yearRange()],
    };
  },

  data() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return {
      isLoading: false,
      viewMode: 'calendar',
      selectedDate: today,
      visibleMonth: today.getMonth(),
      visibleYear: today.getFullYear(),
      dayAttributes: [],
      events: [],
      holidays: [],
      companyEvents: [],
      upcomingHolidays: [],
      highlightedHolidayDate: null,
      highlightTimeoutId: null,
      holidayTooltip: null,
      firstDayOfWeek: 1,
    };
  },

  computed: {
    isEmpty() {
      return this.sortedHolidays.length === 0;
    },
    sortedHolidays() {
      const today = formatDate(new Date(), 'yyyy-MM-dd');

      return this.upcomingHolidays
        .filter((holiday) => holiday.date >= today)
        .sort((a, b) => a.date.localeCompare(b.date));
    },
    groupedHolidays() {
      const groups = [];

      this.sortedHolidays.forEach((holiday) => {
        const date = parseDate(holiday.date, 'yyyy-MM-dd');
        const key = formatDate(date, 'yyyy-MM');
        const lastGroup = groups[groups.length - 1];

        if (lastGroup?.key === key) {
          lastGroup.holidays.push(holiday);
          return;
        }

        groups.push({
          key,
          label: formatDate(date, 'MMMM yyyy', {locale: this.locale}),
          holidays: [holiday],
        });
      });

      return groups;
    },
    isCalendarView: {
      get() {
        return this.viewMode === 'calendar';
      },
      set(isCalendarView) {
        this.viewMode = isCalendarView ? 'calendar' : 'list';
      },
    },
    widgetTitle() {
      return this.viewMode === 'calendar'
        ? this.$t('dashboard.calendar')
        : this.$t('dashboard.holiday_list');
    },
    widgetIcon() {
      return this.viewMode === 'calendar' ? 'calendar3' : 'list-ul';
    },
    holidayTooltipStyle() {
      if (!this.holidayTooltip) {
        return {};
      }

      const {top, left, offsetX, placement, arrowOffset} = this.holidayTooltip;
      const translateY = placement === 'bottom' ? '0' : '-100%';

      return {
        top: `${top}px`,
        left: `${left}px`,
        transform: `translate(calc(-50% + ${offsetX}px), ${translateY})`,
        '--tooltip-arrow-offset': `${arrowOffset}px`,
      };
    },
    holidayIcsFeedUrl() {
      return `${window.appGlobal.baseUrl}/leave/starholiday.ics`;
    },
    googleCalendarSubscribeUrl() {
      const calendarId = this.extractGoogleCalendarId(
        this.googleCalendarEmbedUrl,
      );
      if (!calendarId) {
        return null;
      }

      return `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(
        calendarId,
      )}`;
    },
  },

  watch: {
    viewMode(mode) {
      if (mode === 'calendar') {
        this.$nextTick(() => this.bindCalendarDropdownScrollFix());
      } else {
        this.unbindCalendarDropdownScrollFix();
        this.hideHolidayTooltip();
      }
    },
    isLoading(loading) {
      if (!loading && this.viewMode === 'calendar') {
        this.$nextTick(() => this.bindCalendarDropdownScrollFix());
      }
    },
  },

  mounted() {
    if (!this.isLoading && this.viewMode === 'calendar') {
      this.bindCalendarDropdownScrollFix();
    }
  },

  beforeMount() {
    this.fetchTimesheetWeekStart();
    this.fetchWorkWeek();
    this.isLoading = true;
    Promise.all([
      this.loadHolidaysForMonth(this.visibleYear, this.visibleMonth),
      this.loadUpcomingHolidays(),
    ]).finally(() => {
      this.isLoading = false;
    });
  },

  beforeUnmount() {
    if (this.highlightTimeoutId != null) {
      clearTimeout(this.highlightTimeoutId);
    }
    this.unbindCalendarDropdownScrollFix();
  },

  methods: {
    getHolidayIcsFeedAbsoluteUrl() {
      const basePath = String(window.appGlobal.baseUrl ?? '').replace(
        /^https?:\/\/[^/]+/,
        '',
      );
      const normalizedBase = basePath.replace(/\/$/, '');

      return `${window.location.origin}${normalizedBase}/leave/starholiday.ics`;
    },

    async copyHolidayIcsFeedUrl() {
      const feedUrl = this.getHolidayIcsFeedAbsoluteUrl();

      try {
        await this.writeTextToClipboard(feedUrl);
        this.$toast.success({
          title: this.$t('dashboard.holiday_ics_feed_copied'),
          message: this.$t('dashboard.holiday_ics_feed_copied_hint'),
        });
      } catch {
        this.$toast.error({
          title: this.$t('general.error'),
          message: this.$t('dashboard.holiday_ics_feed_copy_failed'),
        });
      }
    },

    async writeTextToClipboard(text) {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return;
      }

      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();

      try {
        if (!document.execCommand('copy')) {
          throw new Error('Copy command failed');
        }
      } finally {
        document.body.removeChild(textarea);
      }
    },

    extractGoogleCalendarId(embedUrl) {
      if (!embedUrl?.trim()) {
        return null;
      }

      try {
        const src = new URL(embedUrl).searchParams.get('src');
        return src?.trim() || null;
      } catch {
        return null;
      }
    },
    bindCalendarDropdownScrollFix() {
      this.unbindCalendarDropdownScrollFix();

      const calendarRoot = this.$el?.querySelector(
        '.public-holiday-widget-calendar',
      );
      if (!calendarRoot) {
        return;
      }

      const patchDropdownOptions = (dropdown) => {
        dropdown
          .querySelectorAll('.oxd-calendar-dropdown--option')
          .forEach((option) => {
            if (option.dataset.scrollPatched === 'true') {
              return;
            }

            option.dataset.scrollPatched = 'true';
            option.scrollIntoView = () => {
              const top =
                option.offsetTop -
                dropdown.clientHeight / 2 +
                option.clientHeight / 2;
              dropdown.scrollTop = Math.max(0, top);
            };
          });
      };

      this.onCalendarSelectorClick = (event) => {
        if (
          !event.target.closest(
            '.oxd-calendar-selector-month, .oxd-calendar-selector-year',
          )
        ) {
          return;
        }

        this.preservePageScroll();
      };

      calendarRoot.addEventListener(
        'click',
        this.onCalendarSelectorClick,
        true,
      );

      this.onPastHolidayMouseOver = (event) => {
        const dateEl = event.target.closest('.oxd-calendar-date');
        if (!dateEl) {
          return;
        }

        const dateString = this.getDateStringFromCalendarDateEl(dateEl);
        if (!dateString) {
          return;
        }

        if (this.shouldShowDateTooltip(dateString)) {
          this.showHolidayTooltip(dateString, dateEl);
        }
      };

      this.onPastHolidayMouseOut = (event) => {
        const dateEl = event.target.closest('.oxd-calendar-date');
        if (!dateEl) {
          return;
        }

        const dateString = this.getDateStringFromCalendarDateEl(dateEl);
        if (!dateString || !this.shouldShowDateTooltip(dateString)) {
          return;
        }

        const relatedTarget = event.relatedTarget;
        if (
          relatedTarget instanceof Node &&
          (dateEl.contains(relatedTarget) ||
            relatedTarget.closest?.('.public-holiday-widget-tooltip'))
        ) {
          return;
        }

        this.hideHolidayTooltip();
      };

      calendarRoot.addEventListener('mouseover', this.onPastHolidayMouseOver);
      calendarRoot.addEventListener('mouseout', this.onPastHolidayMouseOut);

      this.calendarDropdownObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType !== Node.ELEMENT_NODE) {
              return;
            }

            if (node.classList?.contains('oxd-calendar-dropdown')) {
              patchDropdownOptions(node);
            }

            node
              .querySelectorAll?.('.oxd-calendar-dropdown')
              .forEach(patchDropdownOptions);
          });
        });
      });

      this.calendarDropdownObserver.observe(calendarRoot, {
        childList: true,
        subtree: true,
      });
    },

    unbindCalendarDropdownScrollFix() {
      const calendarRoot = this.$el?.querySelector(
        '.public-holiday-widget-calendar',
      );

      if (calendarRoot && this.onCalendarSelectorClick) {
        calendarRoot.removeEventListener(
          'click',
          this.onCalendarSelectorClick,
          true,
        );
      }

      if (calendarRoot && this.onPastHolidayMouseOver) {
        calendarRoot.removeEventListener(
          'mouseover',
          this.onPastHolidayMouseOver,
        );
        calendarRoot.removeEventListener(
          'mouseout',
          this.onPastHolidayMouseOut,
        );
      }

      this.onCalendarSelectorClick = null;
      this.onPastHolidayMouseOver = null;
      this.onPastHolidayMouseOut = null;
      this.calendarDropdownObserver?.disconnect();
      this.calendarDropdownObserver = null;
    },

    preservePageScroll() {
      const scrollY = window.scrollY;
      const restore = () => {
        if (window.scrollY !== scrollY) {
          window.scrollTo(0, scrollY);
        }
      };

      requestAnimationFrame(restore);
      requestAnimationFrame(() => requestAnimationFrame(restore));
      [0, 50, 100, 200].forEach((delay) => setTimeout(restore, delay));
    },

    responseValidator(status) {
      return (status >= 200 && status < 300) || status === 403;
    },

    toCalendarFirstDayOfWeek(startDay) {
      const day = parseInt(startDay, 10);
      if (!Number.isFinite(day) || day < 1 || day > 7) {
        return 1;
      }

      return day % 7;
    },

    fetchTimesheetWeekStart() {
      this.http
        .request({
          method: 'GET',
          url: '/api/v2/time/time-sheet-period',
          validateStatus: this.responseValidator,
        })
        .then(({data}) => {
          if (data?.data?.startDay != null) {
            this.firstDayOfWeek = this.toCalendarFirstDayOfWeek(
              data.data.startDay,
            );
          }
        });
    },

    getMonthDateRange(year, month) {
      const lastDay = new Date(year, month + 1, 0).getDate();
      const pad = (value) => String(value).padStart(2, '0');

      return {
        fromDate: `${year}-${pad(month + 1)}-01`,
        toDate: `${year}-${pad(month + 1)}-${pad(lastDay)}`,
      };
    },

    getUpcomingDateRange() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const endYear = today.getFullYear() + 5;

      return {
        fromDate: formatDate(today, 'yyyy-MM-dd'),
        toDate: `${endYear}-12-31`,
      };
    },

    mapCompanyEventItems(items) {
      return items.map((event) => ({
        id: `event-${event.id}`,
        date: event.eventDate,
        name: event.eventName || event.text,
        eventTime: event.eventTime,
        shareId: event.shareId,
        formattedDate: event.eventTime || null,
      }));
    },

    mapHolidayItems(items) {
      return items.map((holiday) => ({
        ...holiday,
        formattedDate: formatDate(
          parseDate(holiday.date, 'yyyy-MM-dd'),
          this.jsDateFormat,
          {
            locale: this.locale,
          },
        ),
      }));
    },

    formatHolidayDayLabel(date) {
      return formatDate(parseDate(date, 'yyyy-MM-dd'), 'EEEE, d', {
        locale: this.locale,
      });
    },

    getTodayDateString() {
      return formatDate(new Date(), 'yyyy-MM-dd');
    },

    isPastHolidayDate(dateString) {
      return dateString < this.getTodayDateString();
    },

    getCompanyEventsForDate(dateString) {
      return this.companyEvents.filter((event) => event.date === dateString);
    },

    isCompanyEventDate(dateString) {
      return this.getCompanyEventsForDate(dateString).length > 0;
    },

    isHolidayDate(dateString) {
      return this.holidays.some((holiday) => holiday.date === dateString);
    },

    isOverlapDate(dateString) {
      return (
        this.isHolidayDate(dateString) && this.isCompanyEventDate(dateString)
      );
    },

    shouldShowDateTooltip(dateString) {
      return (
        this.isOverlapDate(dateString) ||
        this.isCompanyEventDate(dateString) ||
        (this.isPastHolidayDate(dateString) && this.isHolidayDate(dateString))
      );
    },

    formatEventTooltipLine(event) {
      return event.eventTime
        ? `${event.name} · ${event.eventTime}`
        : event.name;
    },

    getHolidayTooltipContent(dateString) {
      const holiday = this.holidays.find((item) => item.date === dateString);
      const events = this.getCompanyEventsForDate(dateString);

      if (holiday && events.length) {
        return {
          primary: holiday.name,
          extraLines: events.map((event) => this.formatEventTooltipLine(event)),
          formattedDate: null,
        };
      }

      if (events.length) {
        const [firstEvent, ...otherEvents] = events;

        return {
          primary: firstEvent.name,
          extraLines: otherEvents.map((event) =>
            this.formatEventTooltipLine(event),
          ),
          formattedDate: firstEvent.eventTime ?? null,
        };
      }

      if (holiday) {
        return {
          primary: holiday.name,
          extraLines: [],
          formattedDate: holiday.formattedDate,
        };
      }

      return null;
    },

    buildCalendarEvents(holidayItems, eventItems) {
      const holidayByDate = new Map(
        holidayItems.map((holiday) => [holiday.date, holiday]),
      );
      const eventsByDate = new Map();

      eventItems.forEach((event) => {
        if (!eventsByDate.has(event.eventDate)) {
          eventsByDate.set(event.eventDate, []);
        }

        eventsByDate.get(event.eventDate).push(event);
      });

      const allDates = new Set([
        ...holidayByDate.keys(),
        ...eventsByDate.keys(),
      ]);

      return [...allDates].map((dateString) => {
        const holiday = holidayByDate.get(dateString);
        const events = eventsByDate.get(dateString) ?? [];
        const hasHoliday = Boolean(holiday);
        const hasEvent = events.length > 0;

        let className = '--company-event';
        if (hasHoliday && hasEvent) {
          className = '--holiday-event-overlap';
        } else if (hasHoliday) {
          className = '--public-holiday';
        }

        const eventLabel = events
          .map((event) => event.eventName || event.text)
          .join(', ');

        return {
          date: parseDate(dateString, 'yyyy-MM-dd'),
          type:
            hasHoliday && hasEvent
              ? `${holiday.name} / ${eventLabel}`
              : holiday?.name ?? eventLabel,
          class: className,
        };
      });
    },

    navigateToBuzzEvent(shareId) {
      navigateToBuzzNotification({share: {id: shareId}});
    },

    getDateStringFromCalendarDateEl(dateEl) {
      const dayNumber = parseInt(dateEl.textContent?.trim(), 10);
      if (!Number.isFinite(dayNumber)) {
        return null;
      }

      const pad = (value) => String(value).padStart(2, '0');

      return `${this.visibleYear}-${pad(this.visibleMonth + 1)}-${pad(
        dayNumber,
      )}`;
    },

    showHolidayTooltip(dateString, anchorEl) {
      const tooltipContent = this.getHolidayTooltipContent(dateString);
      const calendarRoot = this.$el?.querySelector(
        '.public-holiday-widget-calendar',
      );

      if (!tooltipContent || !calendarRoot || !anchorEl) {
        return;
      }

      const dateRect = anchorEl.getBoundingClientRect();
      const calendarRect = calendarRoot.getBoundingClientRect();
      const anchorCenterX =
        dateRect.left - calendarRect.left + dateRect.width / 2;
      const anchorTop = dateRect.top - calendarRect.top;

      this.holidayTooltip = {
        ...tooltipContent,
        top: anchorTop - 8,
        left: anchorCenterX,
        offsetX: 0,
        arrowOffset: 0,
        placement: 'top',
      };

      this.$nextTick(() => {
        this.adjustHolidayTooltipPosition(anchorEl);
      });
    },

    adjustHolidayTooltipPosition(anchorEl) {
      const tooltipEl = this.$refs.holidayTooltipRef;
      const scrollContainer = this.$el?.querySelector(
        '.orangehrm-dashboard-widget-body',
      );
      const calendarRoot = this.$el?.querySelector(
        '.public-holiday-widget-calendar',
      );

      if (
        !tooltipEl ||
        !scrollContainer ||
        !calendarRoot ||
        !this.holidayTooltip ||
        !anchorEl
      ) {
        return;
      }

      const padding = 8;
      const containerRect = scrollContainer.getBoundingClientRect();
      const calendarRect = calendarRoot.getBoundingClientRect();
      const dateRect = anchorEl.getBoundingClientRect();
      const tooltipWidth = tooltipEl.offsetWidth;
      const tooltipHeight = tooltipEl.offsetHeight;

      const anchorCenterX =
        dateRect.left - calendarRect.left + dateRect.width / 2;
      const anchorTop = dateRect.top - calendarRect.top;
      const anchorBottom = dateRect.bottom - calendarRect.top;

      const minX = containerRect.left - calendarRect.left + padding;
      const maxX = containerRect.right - calendarRect.left - padding;
      const minY = containerRect.top - calendarRect.top + padding;
      const maxY = containerRect.bottom - calendarRect.top - padding;

      let left = anchorCenterX;
      let offsetX = 0;
      let placement = 'top';
      let top = anchorTop - 8;

      const halfWidth = tooltipWidth / 2;
      if (left - halfWidth < minX) {
        offsetX = minX - (left - halfWidth);
      } else if (left + halfWidth > maxX) {
        offsetX = maxX - (left + halfWidth);
      }

      const fitsAbove = top - tooltipHeight >= minY;
      const fitsBelow = anchorBottom + 8 + tooltipHeight <= maxY;

      if (!fitsAbove && fitsBelow) {
        placement = 'bottom';
        top = anchorBottom + 8;
      } else if (!fitsAbove && !fitsBelow) {
        top = Math.max(minY + tooltipHeight + 8, minY);
      }

      this.holidayTooltip = {
        ...this.holidayTooltip,
        top,
        left,
        offsetX,
        arrowOffset: -offsetX,
        placement,
      };
    },

    hideHolidayTooltip() {
      this.holidayTooltip = null;
    },

    findPastHolidayDateElement(dayNumber) {
      const dateElements = this.$el?.querySelectorAll(
        '.public-holiday-widget-calendar .oxd-calendar-date',
      );

      if (!dateElements) {
        return null;
      }

      return [...dateElements].find((element) => {
        if (parseInt(element.textContent?.trim(), 10) !== dayNumber) {
          return false;
        }

        const dateString = this.getDateStringFromCalendarDateEl(element);
        return (
          dateString &&
          this.isPastHolidayDate(dateString) &&
          this.holidays.some((holiday) => holiday.date === dateString)
        );
      });
    },

    onCalendarBackgroundClick(event) {
      const dateEl = event.target.closest('.oxd-calendar-date');
      if (dateEl) {
        const dateString = this.getDateStringFromCalendarDateEl(dateEl);
        if (
          dateString &&
          this.isPastHolidayDate(dateString) &&
          this.holidays.some((holiday) => holiday.date === dateString)
        ) {
          return;
        }
      }

      if (event.target.closest('.public-holiday-widget-tooltip')) {
        return;
      }

      this.hideHolidayTooltip();
    },

    fetchWorkWeek() {
      this.http
        .request({
          method: 'GET',
          url: '/api/v2/leave/workweek',
          params: {model: 'indexed'},
          validateStatus: this.responseValidator,
        })
        .then(({data}) => {
          if (!data?.data) {
            return;
          }

          this.dayAttributes = Object.keys(data.data).map((index) => {
            const length = data.data[index];
            return {
              index: parseInt(index, 10),
              class:
                length === 8
                  ? '--non-working-day'
                  : length === 4
                  ? '--working-day-half'
                  : '',
            };
          });
        });
    },

    onVisibleMonthChange({month, year}) {
      if (this.visibleMonth === month && this.visibleYear === year) {
        return;
      }

      this.visibleMonth = month;
      this.visibleYear = year;
      this.hideHolidayTooltip();
      this.loadHolidaysForMonth(year, month);
    },

    onCalendarDateSelect(date) {
      if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        return;
      }

      const selectedDate = formatDate(date, 'yyyy-MM-dd');

      if (this.isCompanyEventDate(selectedDate)) {
        const companyEvent = this.getCompanyEventsForDate(selectedDate)[0];
        this.hideHolidayTooltip();
        this.navigateToBuzzEvent(companyEvent?.shareId);
        return;
      }

      const hasHoliday = this.holidays.some(
        (holiday) => holiday.date === selectedDate,
      );

      if (!hasHoliday) {
        this.hideHolidayTooltip();
        return;
      }

      if (this.isPastHolidayDate(selectedDate)) {
        this.$nextTick(() => {
          const dayNumber = parseDate(selectedDate, 'yyyy-MM-dd').getDate();
          const dateEl = this.findPastHolidayDateElement(dayNumber);
          if (dateEl) {
            this.showHolidayTooltip(selectedDate, dateEl);
          }
        });
        return;
      }

      this.hideHolidayTooltip();
      this.viewMode = 'list';
      this.highlightHolidayInList(selectedDate);
    },

    highlightHolidayInList(date) {
      this.highlightedHolidayDate = date;
      this.scrollToHolidayInList(date);

      if (this.highlightTimeoutId != null) {
        clearTimeout(this.highlightTimeoutId);
      }

      this.highlightTimeoutId = setTimeout(() => {
        this.highlightedHolidayDate = null;
        this.highlightTimeoutId = null;
      }, 2000);
    },

    scrollToHolidayInList(date) {
      this.$nextTick(() => {
        const element = this.$el.querySelector(`[data-holiday-date="${date}"]`);
        const scrollContainer = this.$el.querySelector(
          '.orangehrm-dashboard-widget-body',
        );

        if (!element || !scrollContainer) {
          return;
        }

        const containerRect = scrollContainer.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();
        const offset =
          elementRect.top - containerRect.top + scrollContainer.scrollTop;
        const top =
          offset - scrollContainer.clientHeight / 2 + element.clientHeight / 2;

        scrollContainer.scrollTo({
          top: Math.max(0, top),
          behavior: 'smooth',
        });
      });
    },

    loadHolidaysForMonth(year, month) {
      const {fromDate, toDate} = this.getMonthDateRange(year, month);

      return Promise.all([
        this.http.request({
          method: 'GET',
          url: '/api/v2/leave/holidays',
          params: {
            fromDate,
            toDate,
            limit: 100,
            offset: 0,
          },
          validateStatus: this.responseValidator,
        }),
        this.http.request({
          method: 'GET',
          url: '/api/v2/buzz/events',
          params: {
            fromDate,
            toDate,
          },
          validateStatus: this.responseValidator,
        }),
      ]).then(([holidayResponse, eventResponse]) => {
        const holidayItems = Array.isArray(holidayResponse?.data?.data)
          ? holidayResponse.data.data
          : [];
        const eventItems = Array.isArray(eventResponse?.data?.data)
          ? eventResponse.data.data
          : [];

        this.holidays = this.mapHolidayItems(holidayItems);
        this.companyEvents = this.mapCompanyEventItems(eventItems);
        this.events = this.buildCalendarEvents(holidayItems, eventItems);
      });
    },

    loadUpcomingHolidays() {
      const {fromDate, toDate} = this.getUpcomingDateRange();

      return this.http
        .request({
          method: 'GET',
          url: '/api/v2/leave/holidays',
          params: {
            fromDate,
            toDate,
            limit: 200,
            offset: 0,
          },
          validateStatus: this.responseValidator,
        })
        .then(({data}) => {
          const holidayItems = Array.isArray(data?.data) ? data.data : [];

          this.upcomingHolidays = this.mapHolidayItems(holidayItems).sort(
            (a, b) => a.date.localeCompare(b.date),
          );
        });
    },
  },
};
</script>

<style src="./public-holiday-widget.scss" lang="scss" scoped></style>

<style lang="scss">
.public-holiday-widget-view-switch-track {
  background-color: rgba(255, 123, 29, 0.25);
}

.public-holiday-widget-view-switch.--calendar
  .public-holiday-widget-view-switch-track {
  background-color: #ff7b1d;
}
</style>
